/**
 * AgentHiveSelect — selector de agente destino del editor de rutinas
 * (REQ-FRT-001; mismo patrón que el selector de agentes del chat,
 * REQ-CHAT-035): búsqueda incremental, secciones Hives/Agentes y solo los
 * agentes con perfil frontera seleccionables.
 *
 * ISS-642: la carga inicial puede limitarse a la primera página, pero la
 * búsqueda es **server-side sobre el conjunto completo** del tenant y los
 * agentes de un hive se muestran **dentro de su hive** cuando coinciden
 * (auto-expandido durante la búsqueda).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { useAgents } from '@/features/agents/hooks/useAgents';
import { useAgentSearch, usePickerHives } from '../hooks/useFrontier';
import type { Agent } from '@/features/agents/types';
import type { Hive } from '@/features/agents/hives/types';

export interface AgentHiveSelectProps {
  value: string | null;
  onChange: (agentId: string, agentName: string) => void;
  disabled?: boolean;
}

function isFrontier(agent: Agent): boolean {
  return agent.frontier_enabled === true;
}

export function AgentHiveSelect({ value, onChange, disabled = false }: AgentHiveSelectProps) {
  const { t } = useI18n();
  const { data: initialAgents } = useAgents({ pageSize: 100 });
  const { data: hives = [] } = usePickerHives();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce: la búsqueda va al servidor (conjunto completo), no al array local.
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const searching = searchTerm.length > 0;
  const { data: searchResults, isFetching } = useAgentSearch(searchTerm);
  const agents: Agent[] = searching ? searchResults ?? [] : initialAgents;

  const agentsById = useMemo(() => {
    const map = new Map<string, Agent>();
    for (const agent of agents) map.set(agent.id, agent);
    return map;
  }, [agents]);

  const membersByHive = useMemo(() => {
    const map = new Map<string, Agent[]>();
    for (const agent of agents) {
      if (agent.hive_id) {
        const list = map.get(agent.hive_id) ?? [];
        list.push(agent);
        map.set(agent.hive_id, list);
      }
    }
    return map;
  }, [agents]);

  const standalone = useMemo(
    () => agents.filter((agent) => !agent.hive_id),
    [agents],
  );

  // Durante la búsqueda solo se muestran hives con miembros coincidentes
  // (los agentes dentro de su hive); sin búsqueda, todos los que tengan
  // miembros en la página inicial.
  const visibleHives = useMemo(
    () => hives.filter((hive: Hive) => membersByHive.has(hive.id)),
    [hives, membersByHive],
  );

  const selected = value ? agentsById.get(value) : undefined;
  const selectedName = selected?.name ?? value ?? null;

  const close = () => {
    setOpen(false);
    setQuery('');
    setSearchTerm('');
  };

  const select = (agent: Agent) => {
    if (!isFrontier(agent)) return;
    onChange(agent.id, agent.name);
    close();
  };

  const renderAgentRow = (agent: Agent) => {
    const frontier = isFrontier(agent);
    return (
      <button
        key={agent.id}
        type="button"
        data-testid={`agent-select-option-${agent.id}`}
        disabled={!frontier}
        onClick={() => select(agent)}
        className={`flex w-full items-start gap-3 rounded-lg p-2 text-left transition-colors ${
          frontier
            ? 'hover:bg-gray-50 dark:hover:bg-gray-800'
            : 'cursor-not-allowed opacity-60'
        }`}
      >
        <span
          className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950"
          aria-hidden="true"
        >
          <Icon name="bot" size={16} className="text-blue-600 dark:text-blue-300" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{agent.name}</span>
          {agent.description && (
            <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
              {agent.description}
            </span>
          )}
          {!frontier && (
            <span
              className="mt-1 inline-block rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-950 dark:text-amber-300"
              data-testid={`agent-select-no-frontier-${agent.id}`}
            >
              {t('frontier.editor.pickerNoFrontier')}
            </span>
          )}
        </span>
      </button>
    );
  };

  const isEmpty = visibleHives.length === 0 && standalone.length === 0;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        data-testid="agent-select-trigger"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center gap-3 rounded-lg border border-gray-300 bg-white p-3 text-left hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:hover:bg-gray-800"
      >
        <span className="flex-1">
          <span className="block truncate text-sm font-medium">
            {selectedName ?? t('frontier.editor.agentPlaceholder')}
          </span>
        </span>
        <Icon name="chevron-down" size={18} className="text-gray-400" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('frontier.editor.pickerTitle')}
          data-testid="agent-select-panel"
          className="absolute left-0 top-full z-40 mt-2 flex max-h-[min(60vh,480px)] w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          <div className="border-b border-gray-200 p-3 dark:border-gray-700">
            <input
              type="search"
              data-testid="agent-select-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('frontier.editor.pickerSearch')}
              aria-label={t('frontier.editor.pickerSearch')}
              className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 pl-3 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {searching && isFetching && (
              <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500">
                <Spinner size="sm" />
              </div>
            )}
            {visibleHives.length > 0 && (
              <section aria-label={t('frontier.editor.pickerHives')}>
                <h3 className="px-2 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t('frontier.editor.pickerHives')} ({visibleHives.length})
                </h3>
                <div className="space-y-1 pb-2">
                  {visibleHives.map((hive) => {
                    const members = membersByHive.get(hive.id) ?? [];
                    const entryAgent = hive.entry_agent_id
                      ? agentsById.get(hive.entry_agent_id)
                      : undefined;
                    // ISS-642: durante la búsqueda el hive se muestra expandido
                    // para que el miembro coincidente sea visible en su hive.
                    const isExpanded = searching ? true : expanded[hive.id] ?? false;
                    const hiveUnavailable = Boolean(entryAgent) && !isFrontier(entryAgent as Agent);
                    return (
                      <div
                        key={hive.id}
                        data-testid={`agent-select-hive-${hive.id}`}
                        className="rounded-xl border border-transparent"
                      >
                        <button
                          type="button"
                          data-testid={`agent-select-hive-select-${hive.id}`}
                          disabled={hiveUnavailable}
                          onClick={() => {
                            if (entryAgent && isFrontier(entryAgent)) {
                              select(entryAgent);
                            } else {
                              setExpanded((current) => ({
                                ...current,
                                [hive.id]: !isExpanded,
                              }));
                            }
                          }}
                          className={`flex w-full items-start gap-3 rounded-xl p-2 text-left transition-colors ${
                            hiveUnavailable
                              ? 'cursor-not-allowed opacity-60'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          <span
                            className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-950"
                            aria-hidden="true"
                          >
                            <Icon name="server" size={20} className="text-blue-600 dark:text-blue-300" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">{hive.name}</span>
                            {hive.description && (
                              <span className="mt-0.5 block line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                                {hive.description}
                              </span>
                            )}
                            <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                              {t('frontier.editor.pickerViewAgents', { count: members.length })}
                            </span>
                            {hiveUnavailable && (
                              <span
                                className="mt-1 inline-block rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                                data-testid={`agent-select-hive-no-frontier-${hive.id}`}
                              >
                                {t('frontier.editor.pickerNoFrontier')}
                              </span>
                            )}
                          </span>
                        </button>
                        <div className="flex items-center justify-between pb-1 pl-14 pr-2">
                          <button
                            type="button"
                            data-testid={`agent-select-hive-toggle-${hive.id}`}
                            aria-expanded={isExpanded}
                            onClick={() =>
                              setExpanded((current) => ({
                                ...current,
                                [hive.id]: !isExpanded,
                              }))
                            }
                            className="inline-flex items-center gap-1 rounded text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
                          >
                            {isExpanded
                              ? t('frontier.editor.pickerHideAgents')
                              : t('frontier.editor.pickerViewAgents', { count: members.length })}
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="mt-0.5 space-y-0.5 pb-1">
                            {members.map(renderAgentRow)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {standalone.length > 0 && (
              <section aria-label={t('frontier.editor.pickerAgents')}>
                <h3 className="px-2 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t('frontier.editor.pickerAgents')} ({standalone.length})
                </h3>
                <div className="space-y-1">{standalone.map(renderAgentRow)}</div>
              </section>
            )}

            {isEmpty && !isFetching && (
              <p className="px-3 py-6 text-center text-sm text-gray-500">
                {searching ? t('frontier.editor.pickerNoResults') : t('frontier.editor.pickerEmpty')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
